import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/apiClient';

const IntentModal = ({ projectId, intent, onClose, onSave, canManage = true, hasReadOnlyAccess = false }) => {
  const { isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    utterances: [''],
    priority: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form data
  useEffect(() => {
    if (intent) {
      setFormData({
        name: intent.name || '',
        description: intent.description || '',
        utterances: intent.utterances?.length > 0 ? intent.utterances : [''],
        priority: intent.priority || 0
      });
    }
  }, [intent]);

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle utterance changes
  const handleUtteranceChange = (index, value) => {
    const newUtterances = [...formData.utterances];
    newUtterances[index] = value;
    setFormData(prev => ({
      ...prev,
      utterances: newUtterances
    }));
  };

  // Add new utterance
  const addUtterance = () => {
    setFormData(prev => ({
      ...prev,
      utterances: [...prev.utterances, '']
    }));
  };

  // Remove utterance
  const removeUtterance = (index) => {
    if (formData.utterances.length > 1) {
      const newUtterances = formData.utterances.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        utterances: newUtterances
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Filter out empty utterances
      const cleanedUtterances = formData.utterances.filter(u => u.trim() !== '');
      
      // For LLM-based systems, description is most important
      // For rule-based systems, utterances are essential
      if (!formData.description.trim()) {
        setError('Description is required for intent classification');
        setLoading(false);
        return;
      }
      
      if (cleanedUtterances.length === 0 && !formData.description.trim()) {
        setError('Either description or at least one utterance is required');
        setLoading(false);
        return;
      }

      const submitData = {
        ...formData,
        utterances: cleanedUtterances
      };

      if (intent) {
        // Update existing intent
        await apiClient.put(`/flow-builder/projects/${projectId}/intents/${intent._id}`, submitData);
      } else {
        // Create new intent
        await apiClient.post(`/flow-builder/projects/${projectId}/intents`, submitData);
      }

      onSave();
    } catch (err) {
      console.error('Error saving intent:', err);
      setError(err.response?.data?.message || 'Failed to save intent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {intent ? 'Edit Intent' : 'Create Intent'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-blue-500 text-lg">💡</span>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Intent Detection Methods
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p className="mb-1">
                    <strong>🤖 LLM-based (Recommended):</strong> Focus on writing a clear description of when to trigger this intent. The AI understands context and variations naturally.
                  </p>
                  <p>
                    <strong>🔧 Rule-based (Fallback):</strong> Relies on exact sample utterances for pattern matching. Requires comprehensive examples.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Intent Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Intent Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., book_appointment, check_balance"
              required
              disabled={hasReadOnlyAccess}
              readOnly={hasReadOnlyAccess}
            />
            <p className="text-xs text-gray-500 mt-1">
              Use lowercase with underscores (snake_case)
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Describe when this intent should be triggered. E.g., 'When the user wants to find a doctor at a specific hospital. The user might provide the doctor specialty or their symptoms.'"
              required
              disabled={hasReadOnlyAccess}
              readOnly={hasReadOnlyAccess}
            />
            <p className="text-xs text-gray-500 mt-1">
              📝 <strong>Primary for LLM:</strong> Describe when to use this intent. The AI uses this description to understand user intent contextually.
            </p>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Priority (0-100)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.priority}
              onChange={(e) => handleChange('priority', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              disabled={hasReadOnlyAccess}
              readOnly={hasReadOnlyAccess}
            />
            <p className="text-xs text-gray-500 mt-1">
              Higher priority intents are matched first
            </p>
          </div>

          {/* Sample Utterances */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Sample Utterances (Optional for LLM)
              </label>
              {!hasReadOnlyAccess && (
                <button
                  type="button"
                  onClick={addUtterance}
                  className="text-indigo-600 hover:text-indigo-800 text-sm"
                >
                  + Add utterance
                </button>
              )}
            </div>
            
            <div className="space-y-2">
              {formData.utterances.map((utterance, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={utterance}
                    onChange={(e) => handleUtteranceChange(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={`Example: "I need a doctor at Sunter hospital"`}
                    disabled={hasReadOnlyAccess}
                    readOnly={hasReadOnlyAccess}
                  />
                  {formData.utterances.length > 1 && !hasReadOnlyAccess && (
                    <button
                      type="button"
                      onClick={() => removeUtterance(index)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Remove utterance"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            <p className="text-xs text-gray-500 mt-1">
              🤖 <strong>For LLM systems:</strong> The description above is more important. These examples help fine-tune detection.
              <br />
              🔧 <strong>For rule-based systems:</strong> These are essential for pattern matching.
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              {hasReadOnlyAccess ? 'Close' : 'Cancel'}
            </button>
            {canManage && (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : (intent ? 'Update Intent' : 'Create Intent')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default IntentModal; 